import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HeartHandshake, CheckCircle2, FileUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.heic";

export default function PublicVisitRequest() {
  const [orgs, setOrgs] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [docFiles, setDocFiles] = useState({});
  const [extraFiles, setExtraFiles] = useState([]);
  const [done, setDone] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    organization_id: "", guest_name: "", guest_email: "", guest_phone: "",
    arrival_date: "", departure_date: "", party_size: 1,
    visiting_whom: "", purpose: "", is_medical_work: false, department: "", specialty: "",
  });
  const up = (p) => setForm((f) => ({ ...f, ...p }));

  useEffect(() => {
    axios.get(`${API}/public/orgs`).then(({ data }) => {
      setOrgs(data || []);
      if ((data || []).length === 1) setForm((f) => ({ ...f, organization_id: data[0].id }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.is_medical_work) return;
    const params = form.organization_id ? { organization_id: form.organization_id } : {};
    axios.get(`${API}/public/medical-doc-checklist`, { params })
      .then(({ data }) => setChecklist(data.enabled ? data.required_docs || [] : []))
      .catch(() => setChecklist([]));
  }, [form.is_medical_work, form.organization_id]);

  const pendingFiles = () => {
    const list = [];
    Object.entries(docFiles).forEach(([req, f]) => { if (f) list.push({ file: f, requirement: req }); });
    extraFiles.forEach((f) => list.push({ file: f, requirement: null }));
    return list;
  };

  const uploadDocs = async (requestId, token) => {
    const files = pendingFiles();
    if (!files.length) return { uploaded: 0, failed: [] };
    setUploading(true);
    let uploaded = 0; const failed = [];
    for (const { file, requirement } of files) {
      const fd = new FormData();
      fd.append("token", token);
      if (requirement) fd.append("requirement", requirement);
      fd.append("file", file);
      try {
        await axios.post(`${API}/public/visit-request/${requestId}/documents`, fd);
        uploaded += 1;
      } catch (err) {
        failed.push({ name: file.name, reason: err.response?.data?.detail || "Upload failed" });
      }
    }
    setUploading(false);
    return { uploaded, failed };
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/visit-request`, { ...form, party_size: Number(form.party_size) || 1 });
      if (data.type === "medical_request" && data.upload_token) {
        const result = await uploadDocs(data.request_id, data.upload_token);
        setUploadResult(result);
      }
      setDone(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed — please check the form");
    } finally { setSubmitting(false); }
  };

  if (done) return (
    <div className="min-h-screen bg-[#F4F4F1] flex items-center justify-center p-6">
      <Card className="max-w-md w-full border-[#E8E8E6]" data-testid="public-visit-success">
        <CardContent className="py-10 text-center space-y-3">
          <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
          <h2 className="text-xl font-semibold">Request received</h2>
          <p className="text-sm text-[#525C58]">{done.message}</p>
          {uploadResult && uploadResult.uploaded > 0 && (
            <p className="text-sm text-emerald-700" data-testid="upload-success-line">
              {uploadResult.uploaded} document{uploadResult.uploaded > 1 ? "s" : ""} uploaded successfully.
            </p>
          )}
          {uploadResult && uploadResult.failed.length > 0 && (
            <div className="text-left text-xs bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="upload-failed-block">
              <p className="font-medium text-amber-800 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Some documents didn't upload:</p>
              {uploadResult.failed.map((f, i) => <p key={i} className="text-amber-700 mt-1">{f.name} — {f.reason}</p>)}
              <p className="text-amber-700 mt-1">The hospital will follow up with you about any missing documents.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F4F1] py-10 px-4">
      <div className="max-w-xl mx-auto space-y-6">
        <div className="text-center">
          <HeartHandshake className="h-10 w-10 text-[#1B4332] mx-auto mb-2" />
          <h1 className="text-3xl font-semibold" style={{ fontFamily: "Manrope, sans-serif" }}>Plan your visit</h1>
          <p className="text-[#525C58] text-sm mt-1">No account needed — tell us about your stay and we'll take it from there.</p>
        </div>
        <Card className="border-[#E8E8E6]">
          <CardContent className="pt-6">
            <form onSubmit={submit} className="space-y-4" data-testid="public-visit-form">
              {orgs.length > 1 && (
                <div className="space-y-1"><Label>Which hospital are you visiting? *</Label>
                  <Select value={form.organization_id} onValueChange={(v) => up({ organization_id: v })}>
                    <SelectTrigger data-testid="pv-org-select"><SelectValue placeholder="Choose hospital" /></SelectTrigger>
                    <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.display_name || o.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Your name *</Label>
                  <Input required value={form.guest_name} onChange={(e) => up({ guest_name: e.target.value })} data-testid="pv-name" /></div>
                <div className="space-y-1"><Label>Email</Label>
                  <Input type="email" value={form.guest_email} onChange={(e) => up({ guest_email: e.target.value })} data-testid="pv-email" /></div>
                <div className="space-y-1"><Label>Phone</Label>
                  <Input value={form.guest_phone} onChange={(e) => up({ guest_phone: e.target.value })} data-testid="pv-phone" /></div>
                <div className="space-y-1"><Label>Party size</Label>
                  <Input type="number" min={1} value={form.party_size} onChange={(e) => up({ party_size: e.target.value })} data-testid="pv-party" /></div>
                <div className="space-y-1"><Label>Arrival date *</Label>
                  <Input required type="date" value={form.arrival_date} onChange={(e) => up({ arrival_date: e.target.value })} data-testid="pv-arrival" /></div>
                <div className="space-y-1"><Label>Departure date *</Label>
                  <Input required type="date" value={form.departure_date} onChange={(e) => up({ departure_date: e.target.value })} data-testid="pv-departure" /></div>
              </div>
              <div className="space-y-1"><Label>Who are you visiting? (your point person at the hospital) *</Label>
                <Input required value={form.visiting_whom} onChange={(e) => up({ visiting_whom: e.target.value })}
                       placeholder="e.g. Dr. James Ruto, Maintenance dept" data-testid="pv-visiting-whom" /></div>
              <div className="space-y-1"><Label>Purpose of your visit</Label>
                <Textarea rows={2} value={form.purpose} onChange={(e) => up({ purpose: e.target.value })} data-testid="pv-purpose" /></div>
              <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-[#E8E8E6] p-3">
                <input type="checkbox" checked={form.is_medical_work} className="mt-0.5"
                       onChange={(e) => up({ is_medical_work: e.target.checked })} data-testid="pv-medical-toggle" />
                <span className="text-sm">
                  <strong>I will be doing medical work or working at the hospital</strong>
                  <span className="block text-xs text-[#7A8A84]">This routes your request through the hospital's medical approval process.</span>
                </span>
              </label>
              {form.is_medical_work && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1"><Label>Department *</Label>
                      <Input required value={form.department} onChange={(e) => up({ department: e.target.value })}
                             placeholder="e.g. Medicine, Pediatrics, Surgery" data-testid="pv-department" /></div>
                    <div className="space-y-1"><Label>Specialty / role</Label>
                      <Input value={form.specialty} onChange={(e) => up({ specialty: e.target.value })}
                             placeholder="e.g. Cardiology, Nurse" data-testid="pv-specialty" /></div>
                  </div>
                  <div className="rounded-lg border border-[#E8E8E6] bg-white p-3 space-y-3" data-testid="pv-doc-section">
                    <div className="flex items-center gap-2">
                      <FileUp className="h-4 w-4 text-[#1B4332]" />
                      <span className="text-sm font-medium">Licensing documents</span>
                    </div>
                    <p className="text-xs text-[#7A8A84]">
                      Upload what you have now — the hospital will follow up on anything missing. PDF, Word or images, max 10 MB each.
                    </p>
                    {checklist.map((req) => (
                      <div key={req} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                        <span className="text-xs font-medium sm:w-52 shrink-0">{req}</span>
                        <Input type="file" accept={ACCEPT} className="text-xs h-9"
                               onChange={(e) => setDocFiles((d) => ({ ...d, [req]: e.target.files[0] || null }))}
                               data-testid={`pv-doc-${req.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`} />
                      </div>
                    ))}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                      <span className="text-xs font-medium sm:w-52 shrink-0">Other documents</span>
                      <Input type="file" accept={ACCEPT} multiple className="text-xs h-9"
                             onChange={(e) => setExtraFiles(Array.from(e.target.files || []))}
                             data-testid="pv-doc-other" />
                    </div>
                    {pendingFiles().length > 0 && (
                      <p className="text-xs text-emerald-700" data-testid="pv-doc-count">
                        {pendingFiles().length} file{pendingFiles().length > 1 ? "s" : ""} ready — they'll upload when you submit.
                      </p>
                    )}
                  </div>
                </>
              )}
              <Button type="submit" disabled={submitting || uploading} className="w-full bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="pv-submit">
                {uploading ? "Uploading documents…" : submitting ? "Submitting…" : "Submit visit request"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
